import { Vector2 } from '../../../types/game.types';
import { MazeSystem } from './MazeSystem';

interface PathNode {
  position: Vector2;
  gCost: number; // Distance from start
  hCost: number; // Distance to target
  fCost: number; // Total cost
  parent: PathNode | null;
}

export class PathFinding {
  private mazeSystem: MazeSystem;

  constructor(mazeSystem: MazeSystem) {
    this.mazeSystem = mazeSystem;
  }

  // A* pathfinding algorithm
  public findPath(start: Vector2, target: Vector2): Vector2[] {
    const openSet: PathNode[] = [];
    const closedSet: Set<string> = new Set();
    
    const startNode: PathNode = {
      position: start,
      gCost: 0,
      hCost: this.getDistance(start, target),
      fCost: 0,
      parent: null
    };
    startNode.fCost = startNode.gCost + startNode.hCost;
    
    openSet.push(startNode);
    
    while (openSet.length > 0) {
      // Find node with lowest fCost
      let currentNode = openSet[0];
      for (let i = 1; i < openSet.length; i++) {
        if (openSet[i].fCost < currentNode.fCost || 
           (openSet[i].fCost === currentNode.fCost && openSet[i].hCost < currentNode.hCost)) {
          currentNode = openSet[i];
        }
      }
      
      // Remove current from open set
      const currentIndex = openSet.indexOf(currentNode);
      openSet.splice(currentIndex, 1);
      closedSet.add(this.positionKey(currentNode.position));
      
      // Check if we reached the target
      if (currentNode.position.x === target.x && currentNode.position.y === target.y) {
        return this.reconstructPath(currentNode);
      }
      
      // Check neighbors
      const neighbors = this.getNeighbors(currentNode.position);
      for (const neighbor of neighbors) {
        const neighborKey = this.positionKey(neighbor);
        if (closedSet.has(neighborKey)) continue;
        
        const gCost = currentNode.gCost + 1;
        const hCost = this.getDistance(neighbor, target);
        const fCost = gCost + hCost;
        
        // Check if this path to neighbor is better
        let neighborNode = openSet.find(node => 
          node.position.x === neighbor.x && node.position.y === neighbor.y
        );
        
        if (!neighborNode) {
          neighborNode = {
            position: neighbor,
            gCost: gCost,
            hCost: hCost,
            fCost: fCost,
            parent: currentNode
          };
          openSet.push(neighborNode);
        } else if (gCost < neighborNode.gCost) {
          neighborNode.gCost = gCost;
          neighborNode.fCost = fCost;
          neighborNode.parent = currentNode;
        }
      }
    }
    
    // No path found
    return [];
  }

  private getNeighbors(position: Vector2): Vector2[] {
    const neighbors: Vector2[] = [];
    const directions = [
      { x: 0, y: -1 }, // Up
      { x: 1, y: 0 },  // Right
      { x: 0, y: 1 },  // Down
      { x: -1, y: 0 }  // Left
    ];
    
    for (const dir of directions) {
      const newPos = {
        x: position.x + dir.x,
        y: position.y + dir.y
      };
      
      // Handle tunnel wrapping
      const wrappedPos = this.mazeSystem.handleTunnelWrap(newPos.x, newPos.y);
      
      if (this.mazeSystem.isWalkable(wrappedPos.x, wrappedPos.y)) {
        neighbors.push(wrappedPos);
      }
    }
    
    return neighbors;
  }

  private getDistance(pos1: Vector2, pos2: Vector2): number {
    // Manhattan distance (better for grid-based movement)
    const dx = Math.abs(pos1.x - pos2.x);
    const dy = Math.abs(pos1.y - pos2.y);
    
    // Handle tunnel wrapping distance
    const mazeWidth = this.mazeSystem.getMazeWidth();
    const wrappedDx = Math.min(dx, mazeWidth - dx);
    
    return wrappedDx + dy;
  }

  private reconstructPath(node: PathNode): Vector2[] {
    const path: Vector2[] = [];
    let current: PathNode | null = node;
    
    while (current !== null) {
      path.unshift(current.position);
      current = current.parent;
    }
    
    // Remove the starting position
    path.shift();
    
    return path;
  }

  private positionKey(position: Vector2): string {
    return `${position.x},${position.y}`;
  }

  // Get the next direction to move towards a target
  public getDirectionToTarget(from: Vector2, to: Vector2): Vector2 {
    const path = this.findPath(from, to);
    if (path.length === 0) {
      return { x: 0, y: 0 };
    }
    
    const nextStep = path[0];
    return {
      x: nextStep.x - from.x,
      y: nextStep.y - from.y
    };
  }

  // Get direction away from a target (for fleeing)
  public getDirectionAwayFromTarget(from: Vector2, to: Vector2): Vector2 {
    const validDirections = this.mazeSystem.getValidDirections(from.x, from.y);
    if (validDirections.length === 0) {
      return { x: 0, y: 0 };
    }
    
    let bestDirection = validDirections[0];
    let bestDistance = -1;
    
    for (const direction of validDirections) {
      const testPos = {
        x: from.x + direction.x,
        y: from.y + direction.y
      };
      
      const wrappedPos = this.mazeSystem.handleTunnelWrap(testPos.x, testPos.y);
      const distance = this.getDistance(wrappedPos, to);
      
      if (distance > bestDistance) {
        bestDistance = distance;
        bestDirection = direction;
      }
    }
    
    return bestDirection;
  }

  // Get a random valid direction (for scatter mode)
  public getRandomDirection(from: Vector2, excludeOpposite: Vector2 | null = null): Vector2 {
    const validDirections = this.mazeSystem.getValidDirections(from.x, from.y);
    if (validDirections.length === 0) {
      return { x: 0, y: 0 };
    }
    
    // Filter out opposite direction to prevent immediate reversal
    let filteredDirections = validDirections;
    if (excludeOpposite) {
      filteredDirections = validDirections.filter(dir => 
        !(dir.x === -excludeOpposite.x && dir.y === -excludeOpposite.y)
      );
      
      // If no other options, allow opposite direction
      if (filteredDirections.length === 0) {
        filteredDirections = validDirections;
      }
    }
    
    return filteredDirections[Math.floor(Math.random() * filteredDirections.length)];
  }

  // Check if two positions are adjacent
  public areAdjacent(pos1: Vector2, pos2: Vector2): boolean {
    const dx = Math.abs(pos1.x - pos2.x);
    const dy = Math.abs(pos1.y - pos2.y);
    
    // Handle tunnel wrapping
    const mazeWidth = this.mazeSystem.getMazeWidth();
    const wrappedDx = Math.min(dx, mazeWidth - dx);
    
    return (wrappedDx === 1 && dy === 0) || (wrappedDx === 0 && dy === 1);
  }

  // Find the shortest distance between two points (considering tunnels)
  public getShortestDistance(pos1: Vector2, pos2: Vector2): number {
    return this.getDistance(pos1, pos2);
  }
}